// Fungus animation on petri dish
class PetriDish {
  constructor(wrapperSelector = '.homepage-initial', areaSelector = '.grow-area') {
    this.wrapper = document.querySelector(wrapperSelector);
    this.area = document.querySelector(areaSelector);

    if (!this.area || !this.wrapper) {
      console.warn('PetriDish: Wrapper or area element not found.');
      return;
    } 

    this.imgUrl = '/themes/custom/envivent/assets/images/fungus/trail/';
    this.isDisabled = false;
    this.randomDeath = false;

    // Cursor
    this.cursor = document.createElement('div');
    this.cursor.classList.add('cursor');
    this.area.appendChild(this.cursor);

    // Assets
    this.trails = {
      pink: [
        "pink/1.webp",
        "pink/2.webp",
        "pink/3.webp",
        "pink/4.webp",
        "pink/5.webp",
        "pink/6.webp",
        "pink/7.webp",
      ],
      purple: [
        '/purple/8.webp','/purple/9.webp', '/purple/10.webp', '/purple/11.webp', '/purple/12.webp', '/purple/13.webp', '/purple/14.webp', '/purple/15.webp', '/purple/16.webp'
      ]
    };

    // Parameters
    this.durations   = { blue: 500, purple: 500, pink: 200 };
    this.spawnDelay  = 80;
    this.IDLE_START_MS = 3000;
    this.IDLE_SPAWN_MS = 300;
    this.IDLE_SPREAD   = 100;
    this.MIN_LIFE      = 2000;
    this.PLONG         = 0.25;

    // Palette
    this.paletteOrder = ['blue', 'purple', 'pink'];
    this.paletteIdx = 0;
    this.paletteTimeout = null;
    this.paletteValueEl = null;

    // Movement / idle
    this.prevX = null; this.prevY = null;
    this.isInside = false;
    this.lastMoveTs = 0;
    this.idle = false;
    this.idleRAF = null;
    this.lastSpawn = 0;
    this.lastIdleSpawn = 0;
    this.index = 0;

    // Size using moviment speed
    this.MIN_SCALE   = 0.9;  
    this.MAX_SCALE   = 1;  
    this.SPEED_LIMIT = 50;  

    this.activeFungi = new Set();
    this.deathDisabled = false;

    this.setupEvents();
    this.updateCurrentPaletteUI();
    this.cyclePalette();
    requestAnimationFrame(() => this.idleMonitor());
  }

  // Palette
  currentPalette() {
    return this.paletteOrder[this.paletteIdx];
  }

  updateCurrentPaletteUI() {
    if (this.paletteValueEl) this.paletteValueEl.textContent = this.currentPalette();
  }

  cyclePalette() {
    this.updateCurrentPaletteUI();
    if (this.paletteTimeout) clearTimeout(this.paletteTimeout);
    this.paletteTimeout = setTimeout(() => {
      this.paletteIdx = (this.paletteIdx + 1) % this.paletteOrder.length;
      this.updateCurrentPaletteUI();
      this.cyclePalette();
    }, this.durations[this.currentPalette()]);
  }

  // Idle controlling
  idleMonitor() {
    const now = Date.now();
    if (this.isInside && !this.idle && now - this.lastMoveTs > this.IDLE_START_MS) this.startIdle();
    if ((!this.isInside || (this.isInside && now - this.lastMoveTs <= this.IDLE_START_MS)) && this.idle) this.stopIdle();
    requestAnimationFrame(() => this.idleMonitor());
  }

  startIdle() {
    if (this.idle) return;
    this.idle = true;
    this.lastIdleSpawn = performance.now();
    this.idleRAF = requestAnimationFrame((t) => this.idleLoop(t));
  }

  stopIdle() {
    this.idle = false;
    if (this.idleRAF) cancelAnimationFrame(this.idleRAF);
    this.idleRAF = null;
  }

  idleLoop(now) {
    if (!this.idle) return;

    if (now - this.lastIdleSpawn > this.IDLE_SPAWN_MS) {
      const isRandomSeed = Math.random() < 0.3;
      let targetX, targetY;

      if (isRandomSeed || this.activeFungi.size === 0) {
        const rect = this.area.getBoundingClientRect();
        targetX = Math.random() * rect.width;
        targetY = Math.random() * rect.height;
      } else {
        const fungiArray = Array.from(this.activeFungi);
        const chosenFungus = fungiArray[Math.floor(Math.random() * fungiArray.length)];
        const rect = chosenFungus.getBoundingClientRect();
        const parentRect = this.area.getBoundingClientRect();
        const baseX = rect.left - parentRect.left + rect.width / 2;
        const baseY = rect.top - parentRect.top + rect.height / 2;
        targetX = baseX + (Math.random() * this.IDLE_SPREAD - this.IDLE_SPREAD / 2);
        targetY = baseY + (Math.random() * this.IDLE_SPREAD - this.IDLE_SPREAD / 2);
      }

      this.spawnFungus(targetX, targetY, null, null, 0);
      this.lastIdleSpawn = now;
    }

    this.idleRAF = requestAnimationFrame((t) => this.idleLoop(t));
  }

  // Events
  setupEvents() {
    this.area.addEventListener('mouseenter', () => {
      this.isInside = true;
      this.lastMoveTs = Date.now();
    });

    this.area.addEventListener('mousemove', (e) => {
      if (!this.cursor) return;

      this.isInside = true;
      this.lastMoveTs = Date.now();
      this.cursor.style.opacity = 1;

      const rect = this.area.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      this.cursor.style.left = x + 'px';
      this.cursor.style.top  = y + 'px';

      const now = Date.now();
      if (now - this.lastSpawn > this.spawnDelay) {
        const speed = this.prevX === null ? 0 : Math.hypot(x - this.prevX, y - this.prevY);
        this.spawnFungus(x, y, this.prevX, this.prevY, speed);
        this.lastSpawn = now;
        this.prevX = x; this.prevY = y;
      }
    });

    this.area.addEventListener('mouseleave', () => {
      if (!this.cursor) return;

      this.isInside = false;
      this.cursor.style.opacity = 0;
      this.prevX = this.prevY = null;
    });
  }

  spawnFungus(x, y, px, py, speed) {
    if (this.isDisabled) return;
    const palette = this.currentPalette();
    let srcList = this.trails.pink;
    if (palette === 'blue' && this.trails.purple.length) srcList = this.trails.purple;
    if (palette === 'purple' && this.trails.purple.length) srcList = this.trails.purple;

    const fungus = document.createElement('img');
    fungus.src = this.imgUrl + srcList[this.index % srcList.length];
    fungus.classList.add('fungus');

    let dirX = 1, dirY = 0;
    if (this.prevX != null && this.prevY != null) {
        const dist = Math.hypot(x - this.prevX, y - this.prevY) || 1;
        dirX = (x - this.prevX) / dist;
        dirY = (y - this.prevY) / dist;
    }

    const perpX = -dirY;
    const perpY = dirX;
    const lateralOffset = (Math.random() * 14 - 7);
    const backOffset = Math.min(speed || 0, 24);
    const finalX = x - dirX * backOffset + perpX * lateralOffset;
    const finalY = y - dirY * backOffset + perpY * lateralOffset;

    fungus.style.left = `${finalX}px`;
    fungus.style.top  = `${finalY}px`;

    const clampSpeed = Math.min(speed || 0, this.SPEED_LIMIT);
    const factor = 1 - (clampSpeed / this.SPEED_LIMIT);
    const finalScale = this.MIN_SCALE + (this.MAX_SCALE - this.MIN_SCALE) * factor;

    if (palette === 'blue') {
        fungus.style.filter = 'hue-rotate(335deg) saturate(1) brightness(1)';
    } else if (palette === 'purple' && srcList === this.trails.pink) {
        fungus.style.filter = 'hue-rotate(290deg) saturate(1.1)';
    } else {
        fungus.style.filter = '';
    }

    this.area.appendChild(fungus);
    this.activeFungi.add(fungus);
    fungus.offsetWidth;
    requestAnimationFrame(() => {
        fungus.style.transform = `translate(-50%, -50%) scale(${finalScale})`;
    });

    this.index++;

    // Randomized death
    if (this.randomDeath) {
      this.randomizedDeath(fungus);
    } else {
      this.regularizedDeath(fungus);
    }
   
  }

  randomizedDeath(fungus) {
    const exp1 = () => -Math.log(1 - Math.random());

    let extraLife;
    if (Math.random() < this.PLONG) {
      extraLife = this.MIN_LIFE * (1 + Math.random());
    } else {
      extraLife = (exp1() + exp1()) * (this.MIN_LIFE * 0.5);
    }

    const lifetime = this.MIN_LIFE + extraLife;
    const deathJitter = Math.random() * (this.MIN_LIFE * 0.3); 
    const fadeMs = 700 + Math.random() * 1800;

    setTimeout(() => {
      if (this.deathDisabled) return;
      const baseTransition = getComputedStyle(fungus).transition;
      fungus.style.transition = `transform ${fadeMs}ms ease-in, opacity ${fadeMs}ms ease-in${baseTransition ? `, ${baseTransition}` : ''}`;
      fungus.classList.add('dead');
      fungus.addEventListener('transitionend', () => {
        fungus.remove();
        this.activeFungi.delete(fungus);
      }, { once: true });
    }, lifetime + deathJitter);
  }


  regularizedDeath(fungus) {
    setTimeout(() => {
      if (this.deathDisabled) return;
      const baseTransition = getComputedStyle(fungus).transition;
      fungus.style.transition = `transform 1000ms ease-in, opacity 1000ms ease-in${baseTransition ? `, ${baseTransition}` : ''}`;
      fungus.classList.add('dead');
      fungus.addEventListener('transitionend', () => {
        fungus.remove();
        this.activeFungi.delete(fungus);
      }
      , { once: true });
    }, this.MIN_LIFE);
  }

  // Control panel
  createNumberInput(label, initial, min, max, step, onChange) {
    const wrap = document.createElement('label');
    wrap.className = 'control-row';
    const span = document.createElement('span');
    span.textContent = label;
    const input = document.createElement('input');
    input.type = 'number';
    input.value = String(initial);
    if (min != null) input.min = String(min);
    if (max != null) input.max = String(max);
    input.step = String(step ?? 1);
    input.addEventListener('input', () => onChange(Number(input.value)));
    wrap.appendChild(span);
    wrap.appendChild(input);
    return { wrap, input };
  }

  createSection(titleText) {
    const sec = document.createElement('div');
    sec.className = 'control-section';
    const h = document.createElement('h4');
    h.textContent = titleText;
    sec.appendChild(h);
    return sec;
  }

  setupPanel() {
    const controlPanel = document.createElement('div');
    controlPanel.classList.add('fungus-control-panel');

    const paletteInfo = document.createElement('div');
    paletteInfo.className = 'palette-info';
    const paletteLabel = document.createElement('span');
    paletteLabel.textContent = 'Actual color: ';
    const paletteValue = document.createElement('strong');
    paletteValue.textContent = this.currentPalette();
    paletteInfo.appendChild(paletteLabel);
    paletteInfo.appendChild(paletteValue);
    this.paletteValueEl = paletteValue;

    const clearButton = document.createElement('button');
    clearButton.textContent = 'Clear Fungus';
    clearButton.addEventListener('click', () => {
      this.area.querySelectorAll('.fungus').forEach(f => f.remove());
      this.activeFungi.clear();
    });

    const disableDeathButton = document.createElement('button');
    disableDeathButton.textContent = 'Disable Fungus Death';
    disableDeathButton.addEventListener('click', () => {
      this.deathDisabled = !this.deathDisabled;
      disableDeathButton.textContent = this.deathDisabled ? 'Enable Fungus Death' : 'Disable Fungus Death';
    });

    const topRow = document.createElement('div');
    topRow.className = 'control-top-row';
    topRow.appendChild(clearButton);
    topRow.appendChild(disableDeathButton);

    // sections
    const paletteSection = this.createSection('Palette');
    paletteSection.appendChild(this.createNumberInput('Blue (ms)', this.durations.blue, 50, 60000, 10, v => { this.durations.blue = v; this.cyclePalette(); }).wrap);
    paletteSection.appendChild(this.createNumberInput('Purple (ms)', this.durations.purple, 50, 60000, 10, v => { this.durations.purple = v; this.cyclePalette(); }).wrap);
    paletteSection.appendChild(this.createNumberInput('Pink (ms)', this.durations.pink, 50, 60000, 10, v => { this.durations.pink = v; this.cyclePalette(); }).wrap);

    const spawnSection = this.createSection('Active Spawn');
    spawnSection.appendChild(this.createNumberInput('Spawn Delay (ms)', this.spawnDelay, 0, 5000, 10, v => { this.spawnDelay = v; }).wrap);

    const idleSection = this.createSection('Idle');
    idleSection.appendChild(this.createNumberInput('Idle Start (ms)', this.IDLE_START_MS, 0, 600000, 50, v => { this.IDLE_START_MS = v; }).wrap);
    idleSection.appendChild(this.createNumberInput('Idle Spawn (ms)', this.IDLE_SPAWN_MS, 16, 600000, 10, v => { this.IDLE_SPAWN_MS = v; }).wrap);
    idleSection.appendChild(this.createNumberInput('Idle Spread (px)', this.IDLE_SPREAD, 0, 2000, 1, v => { this.IDLE_SPREAD = v; }).wrap);

    const lifeSection = this.createSection('Fungi Life');
    lifeSection.appendChild(this.createNumberInput('Min Life (ms)', this.MIN_LIFE, 0, 300000, 50, v => { this.MIN_LIFE = v; }).wrap);
    lifeSection.appendChild(this.createNumberInput('P(Long) 0..1', this.PLONG, 0, 1, 0.01, v => { this.PLONG = Math.min(1, Math.max(0, v)); }).wrap);
    const deathInputWrap = document.createElement('label');
    deathInputWrap.className = 'control-row';
    const deathSpan = document.createElement('span');
    deathSpan.textContent = 'Random Death';
    const deathInput = document.createElement('input');
    deathInput.type = 'checkbox';
    deathInput.checked = this.randomDeath;
    deathInput.addEventListener('change', () => { this.randomDeath = deathInput.checked; });
    deathInputWrap.appendChild(deathSpan);
    deathInputWrap.appendChild(deathInput);
    lifeSection.appendChild(deathInputWrap);

    const scaleSection = this.createSection('Scale');
    scaleSection.appendChild(this.createNumberInput('Min Scale', this.MIN_SCALE, 0.1, 3, 0.05, v => { this.MIN_SCALE = v; }).wrap);
    scaleSection.appendChild(this.createNumberInput('Max Scale', this.MAX_SCALE, 0.1, 3, 0.05, v => { this.MAX_SCALE = v; }).wrap);
    scaleSection.appendChild(this.createNumberInput('Speed Limit', this.SPEED_LIMIT, 1, 200, 1, v => { this.SPEED_LIMIT = v; }).wrap);
    
    controlPanel.appendChild(paletteInfo);
    controlPanel.appendChild(topRow);
    controlPanel.appendChild(paletteSection);
    controlPanel.appendChild(spawnSection);
    controlPanel.appendChild(idleSection);
    controlPanel.appendChild(lifeSection);
    controlPanel.appendChild(scaleSection);

    this.wrapper.appendChild(controlPanel);
  }

  disablePanel() {
    const panel = this.wrapper.querySelector('.fungus-control-panel');
    if (panel) panel.remove();
  }

  inactivate() {
    this.area.querySelectorAll('.fungus').forEach(f => f.remove());
    this.activeFungi.clear();
    this.isDisabled = true;
    this.cursor.classList.add('disabled');
  }

  activate() {
    this.isDisabled = false;
    this.cursor.classList.remove('disabled');
  }
}

export default PetriDish;
